/**
 * policyUtils.js
 * Centraliza el estado inicial, mapeos de moneda, filtros y lógica de negocio para Patrimoniales.
 * Totalmente integrado con segurosUtils para evitar duplicación de funciones matemáticas.
 */

import { 
    parseCurrency, 
    formatCurrencyForInput, 
    normalizeString, 
    mapMonedaToDB,
    mapMonedaToVisual
} from '../../../utils/segurosUtils';

// Re-exportamos los formateadores para mantener compatibilidad con los archivos que los consumen desde aquí
export { parseCurrency, formatCurrencyForInput, mapMonedaToDB, mapMonedaToVisual };

export const INITIAL_STATE = {
    fechaCarga: new Date().toISOString().split('T')[0],
    estadoEmision: '', 
    nPoliza: '', 
    motivo: 'Póliza Nueva',
    subOperacion: '',
    riesgo: '',
    riesgoId: '',   // Sincronizado dinámicamente desde el maestro de riesgos 
    subriesgo: '', 
    polizaExpirante: '',
    companiaExpirante: '',
    quienRenovo: '',
    type: 'patrimoniales', // Tipo de póliza unificado para auditorías y BI
    
    // --- RENOVACIÓN ---
    formaRenovacion: 'MANUAL', 
    tipoRenovacion: '',       
    inicioVigencia: '', 
    finVigencia: '',
    compania: '', 
    companiaId: '', // Sincronizado dinámicamente desde el maestro de compañías
    organizador: '', 
    pas: '', 
    canal: '', 
    canalNombreId: '', 
    canalNombre: '', 
    subcanalNombreId: '', 
    subcanalNombre: '', 
    vendedor: '', 
    suscriptor: '', 
    tipoConversion: '',
    tieneContador: false, 
    contadorId: '', 
    contadorNombre: '',
    
    // --- FACTURACIÓN ---
    formaPago: '', 
    medioPago: '', 
    tipoFacturacion: '', 
    moneda: '$', 
    primaEmitida: '', 
    premio: '', 
    primaAdelanto: '', 
    primaPrimeraCuota: '',
    cuotas: '', 
    comision: '', 
    comisionOrganizador: 0, 
    comisionAsociado: 0,
    descuentoOrganizador: 0,
    descuentoAsociado: 0,
    
    // --- REGISTRO DE COBRO ---
    pagoEfectuado: false, 
    quienCobro: '', 
    fechaCobro: '',

    fechaDespacho: '', 
    formaDespacho: '',  
    postventa: 'No', 

    // --- GESTIÓN POST VENTA INICIAL ---
    pv_fechaGestion: new Date().toISOString().split('T')[0],
    pv_tipoGestion: '',
    pv_quienGestiono: '',
    pv_comentarios: '',
    pv_fechaProximaGestion: '',

    fechaRefacturacion: '',
    observaciones: '', 
    isPolizaPack: false, 
    referenciasPolizaPack: [],
    
    // --- CAMPOS DINÁMICOS ---
    patentes: [''], 
    ubicacionesRiesgo: [{ direccion: '', m2: '' }],
    actividades: [''], 
    especialidades: [''],

    // --- CAMPOS DE REFACTURACIÓN / ENDOSO 0 ---
    ref_numeroFactura: '',
    ref_inicioVigencia: '', 
    ref_finVigencia: '', 
    ref_moneda: '', 
    ref_estadoGestion: '', 
    ref_numeroEndoso: '',
    ref_tipoEndoso: '', 
    ref_prima: '', 
    ref_primaEmitida: '',
    ref_quienCobro: '', 
    ref_fechaCobro: '', 
    ref_formaDespacho: '', 
    ref_fechaDespacho: '',
    ref_pagoEfectuado: false,
    
    // --- NUEVOS MEDIOS ---
    nuevoBanco: '', 
    nuevoCbu: '', 
    nuevaMarca: '', 
    nuevoNumero: '', 
    nuevoVto: ''
};

/**
 * Normaliza texto eliminando acentos, diacríticos y caracteres especiales,
 * retornando el valor resultante en mayúsculas para simplificar comparaciones.
 * * @param {string} str - Texto a normalizar
 * @returns {string} Texto normalizado en mayúsculas
 */
export const normalizeText = (str) => {
    return normalizeString(str).toUpperCase();
};

/**
 * Normaliza el campo de patentes para soportar la convivencia entre
 * el formato viejo (string "patente") y el nuevo (array "patentes").
 */
export const getNormalizedPatentes = (data) => {
    if (!data) return [''];
    
    if (data.patentes && Array.isArray(data.patentes) && data.patentes.length > 0) {
        return data.patentes;
    }
    
    if (data.patente && typeof data.patente === 'string' && data.patente.trim() !== '') {
        return [data.patente.toUpperCase().trim()];
    }
    
    return [''];
};

/**
 * Determina si el riesgo y subriesgo corresponden a una Caución Especial.
 */
export const checkEsCaucionEspecial = (riesgo, subriesgo) => {
    const r = normalizeText(riesgo);
    const s = normalizeText(subriesgo);
    
    const especiales = [
        'ANTICIPO FINANCIERO', 
        'EJECUCION DE CONTRATO', 
        'SUSTITUCION DE FONDO DE REPARO',
        'MANTENIMIENTO DE OFERTA',
        'OTRAS - VIGENCIA TRIMESTRAL',
        'JUDICIAL',
    ];
    
    return r === 'CAUCION' && especiales.includes(s);
};

/**
 * Filtra y limpia los payloads antes de ser enviados a Firestore,
 * evitando la persistencia de propiedades redundantes de la UI y campos vacíos.
 */
export const cleanPayload = (obj) => {
    const result = { ...obj };

    // Eliminación explícita de campos redundantes de control de la UI
    delete result.fechaUltimaGestion;
    delete result.ultimaModificacionSistema;

    // Filtrado de campos vacíos, nulos o ceros para mantener la BD limpia
    return Object.fromEntries(
        Object.entries(result).filter(([_, v]) => {
            if (v === "" || v === null || v === undefined || v === 0) return false;
            
            if (Array.isArray(v)) {
                const filteredArray = v.filter(item => item !== "" && item !== null && item !== undefined && item !== 0);
                return filteredArray.length > 0;
            }
            return true;
        })
    );
};

/**
 * Calcula automáticamente el estado de la póliza (VIGENTE, VENCIDA, NO INICIADA)
 * protegiendo los estados que requieren intervención manual del operador.
 */
export const calculatePolicyStatus = (startDate, endDate, currentStatus) => {
    const manualStates = [
        'PENDIENTE DE EMISIÓN',
        'PENDIENTE DE INSPECCIÓN',
        'PROPUESTA INGRESADA',
        'ANULADA'
    ];

    if (manualStates.includes(currentStatus)) return currentStatus;
    if (!startDate) return currentStatus;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(startDate + "T00:00:00");
    const end = endDate ? new Date(endDate + "T00:00:00") : null;

    if (today < start) return 'NO INICIADA';
    if (end && today > end) return 'VENCIDA';
    return 'VIGENTE';
};